// Drift metrics carry three different units, so one formatter misreads most of
// them. `findings_per_run` is a rate (findings ÷ runs); the rest are proportions
// in 0..1. The deltas diverge again: `findings_per_run` and `thumbs_down_ratio`
// report a RELATIVE change, while `pct_substantive` and the council-wide floor
// alarm report an absolute percentage-POINT change.
//
// Rendering every row as `baseline → current` plus a `%` delta printed the floor
// alarm as `0.600 → 0.211` / `-38.9%`, which reads as a 39% drop from a trailing
// baseline when it is really 38.9 points below a fixed floor — and mis-signed
// `pct_substantive` the same way.
//
// These mirror fmtValue / fmtDelta / METRIC_LABEL in
// packages/api/src/lib/drift-alert.ts, which already render the Slack and GitHub
// output correctly. Keep the two in step: a new metric needs an entry here too.
//
// Kept in its own module (no DOM imports) so it unit-tests in the node env,
// matching the calibration-repo-label.ts convention — importing calibration.tsx
// from a test pulls in portal-config, which throws when unconfigured.
export const FLOOR_METRIC = 'council_pct_substantive_floor';

// Detector-coverage metrics: these report that a CHECK COULD NOT RUN, so their
// baseline/current are whole counts (findings, slices) rather than rates or
// proportions. A third unit, on top of the two the header describes.
export const DETECTOR_BLIND_COUNCIL_METRIC = 'detector_blind_council_sample';
export const DETECTOR_BLIND_FPR_METRIC = 'detector_blind_findings_per_run';

// A reviewer that emitted nothing across both windows. Every rate on the row is
// zero on both sides, so neither an arrow nor a delta says anything — the runs
// it was silent across are the whole content, and they arrive out of band on
// the alert's `exposure` field.
export const REVIEWER_SILENT_METRIC = 'reviewer_silent';

const PROPORTION_METRICS = new Set(['pct_substantive', 'thumbs_down_ratio', FLOOR_METRIC]);
const POINT_DELTA_METRICS = new Set(['pct_substantive', FLOOR_METRIC]);
const COUNT_METRICS = new Set([
  DETECTOR_BLIND_COUNCIL_METRIC,
  DETECTOR_BLIND_FPR_METRIC,
  REVIEWER_SILENT_METRIC,
]);

const DRIFT_METRIC_LABEL: Record<string, string> = {
  findings_per_run: 'findings per run',
  pct_substantive: 'substantive findings %',
  thumbs_down_ratio: 'thumbs-down ratio',
  [FLOOR_METRIC]: 'substantive findings % vs floor',
  [DETECTOR_BLIND_COUNCIL_METRIC]: 'council findings below sample floor',
  [DETECTOR_BLIND_FPR_METRIC]: 'slices with traffic but no alarmable baseline',
  [REVIEWER_SILENT_METRIC]: 'reviewer emitted nothing',
};

export function driftMetricLabel(metric: string): string {
  return DRIFT_METRIC_LABEL[metric] ?? metric.replace(/_/g, ' ');
}

// The three severity classes a drift row can carry. 'expected-post-change' is a
// row whose reviewer's prompt was deliberately changed inside the baseline
// window, so the shift is that change taking effect (death-star#308).
export type DriftSeverity = 'warning' | 'critical' | 'expected-post-change';

// A reviewer-calibration change point, as it arrives on an annotated alert.
// Mirrors ChangePoint in packages/api/src/lib/calibration-change-points.ts.
export interface DriftChangePoint {
  reviewer: string;
  effective_date: string;
  commit_sha: string;
  pr_number: number | null;
  subject: string;
}

// Hyphens read as a typo in a badge. Spaced out, it reads as prose.
export function driftSeverityLabel(severity: DriftSeverity): string {
  return severity === 'expected-post-change' ? 'expected post-change' : severity;
}

// Where the reviewer change actually landed. Prefers the PR, because that is what
// carries the reasoning; falls back to the short sha for a direct push.
export function changePointLabel(cp: DriftChangePoint): string {
  return cp.pr_number === null
    ? `${cp.commit_sha.slice(0, 7)} (${cp.effective_date})`
    : `engineering-plugins#${cp.pr_number} (${cp.effective_date})`;
}

export function changePointUrl(cp: DriftChangePoint): string {
  const base = 'https://github.com/rewind-corp/engineering-plugins';
  return cp.pr_number === null ? `${base}/commit/${cp.commit_sha}` : `${base}/pull/${cp.pr_number}`;
}

export function fmtDriftValue(metric: string, v: number): string {
  // Counts are whole units. `toFixed(3)` would render 29 slices as "29.000".
  if (COUNT_METRICS.has(metric)) return String(v);
  return PROPORTION_METRICS.has(metric) ? `${(v * 100).toFixed(1)}%` : v.toFixed(3);
}

export function fmtDriftDelta(metric: string, d: number): string {
  const sign = d > 0 ? '+' : '';
  if (metric === FLOOR_METRIC) return `${sign}${(d * 100).toFixed(1)}pp vs floor`;
  // Before the unit check: a count delta through the `%` fallback below printed
  // -29 slices as "-2900.0%".
  if (metric === DETECTOR_BLIND_COUNCIL_METRIC) return `${Math.abs(d)} findings short of floor`;
  if (metric === DETECTOR_BLIND_FPR_METRIC) return `${Math.abs(d)} unalarmable`;
  // The row exists precisely because there is no delta: a reviewer already at
  // zero cannot move relative to a zero baseline. Saying "0.0%" would present
  // that as a measured no-change.
  if (metric === REVIEWER_SILENT_METRIC) return 'no drift to measure';
  if (POINT_DELTA_METRICS.has(metric)) return `${sign}${(d * 100).toFixed(1)}pp`;
  return `${sign}${(d * 100).toFixed(1)}%`;
}

// The floor alarm has no trailing baseline to compare against — `baseline` is the
// constant floor — so the arrow would imply a movement that never happened.
// `exposure` is carried only by rows whose numbers are all zero (reviewer_silent),
// where the runs the row was observed over are the only informative quantity.
export function fmtDriftRange(metric: string, baseline: number, current: number, exposure?: number): string {
  if (metric === REVIEWER_SILENT_METRIC) {
    return exposure === undefined ? 'no findings' : `no findings in ${exposure} runs`;
  }
  if (metric === FLOOR_METRIC) {
    return `${fmtDriftValue(metric, current)} (floor ${fmtDriftValue(metric, baseline)})`;
  }
  // Same reasoning as the floor alarm: these rows report a check that did not
  // run, so `baseline` is the threshold that was missed (or the population that
  // could have been measured), not a previous value the current one moved from.
  if (metric === DETECTOR_BLIND_COUNCIL_METRIC) {
    return `${current} findings (floor ${baseline})`;
  }
  if (metric === DETECTOR_BLIND_FPR_METRIC) {
    return `${current} of ${baseline} slices alarmable`;
  }
  return `${fmtDriftValue(metric, baseline)} → ${fmtDriftValue(metric, current)}`;
}
